import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticationService, resetB2CLoginUrlFetchState } from '../services/authentication.service';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userRoles: string[];
  tenantId: number | null;
  userId: string | null;
  userName: string | null;
  logout: () => void;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { instance } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  const checkAuth = () => {
    console.log('[AuthContext] checkAuth START');
    const authenticated = authenticationService.isAuthorized();
    console.log('[AuthContext] isAuthorized result:', authenticated);
    
    setIsAuthenticated(authenticated);
    if (authenticated) {
      const roles = authenticationService.getUserRoles();
      const tenant = authenticationService.getTenantId();
      const uid = authenticationService.getUserId();
      const uname = authenticationService.getUserName();
      
      console.log('[AuthContext] Setting auth state - roles:', roles, 'tenantId:', tenant, 'userId:', uid, 'userName:', uname);
      
      setUserRoles(roles);
      setTenantId(tenant);
      setUserId(uid);
      setUserName(uname);
    } else {
      console.log('[AuthContext] Not authenticated, clearing state');
      setUserRoles([]);
      setTenantId(null);
      setUserId(null);
      setUserName(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    console.log('[AuthContext] ===== INITIAL MOUNT =====');
    console.log('[AuthContext] localStorage.JwtToken:', localStorage.getItem('JwtToken')?.substring(0, 50) + '...');
    console.log('[AuthContext] localStorage.loginMethod:', localStorage.getItem('loginMethod'));
    console.log('[AuthContext] localStorage.currentUser.roles:', localStorage.getItem('currentUser.roles'));
    console.log('[AuthContext] Running checkAuth...');
    checkAuth();
  }, []);

  // Local login REMOVED 2026-09-12: sign-in is Azure AD B2C only. This called
  // authenticationService.login -> POST /api/login, an unauthenticated endpoint that
  // returned a JWT the whole API trusts, bypassing B2C. The endpoint is switched off.

  const logout = async () => {
    console.log('[AuthContext] logout START');
    // Read loginMethod BEFORE clearing storage — it lives in localStorage
    const loginMethod = authenticationService.getLoginMethod();
    console.log('[AuthContext] loginMethod:', loginMethod);

    resetB2CLoginUrlFetchState();

    // Clear React state
    setIsAuthenticated(false);
    setUserRoles([]);
    setTenantId(null);
    setUserId(null);
    setUserName(null);
    console.log('[AuthContext] Cleared local state');

    // Handle Azure B2C MSAL logout
    if (loginMethod === 'azure-b2c-msal') {
      console.log('[AuthContext] Azure B2C MSAL logout - calling instance.logoutRedirect...');
      try {
        // Wipe app keys (JwtToken, loginMethod, currentUser.*) BEFORE the redirect so they're
        // gone when the post-logout redirect lands back on /login. MSAL's own msal.* cache is
        // preserved so it can build the logout request.
        authenticationService.clearAppStorage();
        await instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + '/login',
        });
        console.log('[AuthContext] MSAL logoutRedirect completed');
      } catch (error) {
        console.error('[AuthContext] MSAL logout error:', error);
        authenticationService.logout();
        window.location.replace('/login');
      }
    } else if (loginMethod === 'azure-b2c') {
      // Legacy Azure B2C (non-MSAL) logout
      console.log('[AuthContext] Legacy Azure B2C logout');
      const logoutUrl = await authenticationService.getAzureB2CLogoutUrl();
      authenticationService.logout();
      if (logoutUrl) {
        console.log('[AuthContext] Redirecting to Azure B2C logout URL');
        window.location.href = logoutUrl;
      } else {
        console.log('[AuthContext] No logout URL, redirecting to /login');
        window.location.replace('/login');
      }
    } else {
      // Local login logout
      console.log('[AuthContext] Local logout - redirecting to /login');
      authenticationService.logout();
      window.location.replace('/login');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        userRoles,
        tenantId,
        userId,
        userName,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
